// =============================================================================
// FILE: MessageInput.js
// PURPOSE:
//   Clean ChatGPT-style message input for Ticket AI Chat
// EXTENDED:
//   - Speech-to-Text (Web Speech API)
//   - Mic icon inside input
//   - Live transcription into input box
// NOTE:
//   Existing behavior is NOT changed or removed
// =============================================================================

import React, { useState, useRef } from "react";

export default function MessageInput({
  onSend,
  disabled = false,
  placeholder = "Ask Ticket AI…",
}) {
  const [message, setMessage] = useState("");
  const [listening, setListening] = useState(false);

  const recognitionRef = useRef(null);

  // ------------------------------------------------------------
  // SEND MESSAGE (UNCHANGED)
  // ------------------------------------------------------------
  const handleSend = () => {
    if (!message.trim() || disabled) return;
    onSend(message.trim());
    setMessage("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ------------------------------------------------------------
  // SPEECH TO TEXT (NEW – SAFE)
  // ------------------------------------------------------------
  const startListening = () => {
    if (disabled) return;

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }

    if (!recognitionRef.current) {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.continuous = false;
      recognition.interimResults = true;

      recognition.onstart = () => {
        setListening(true);
      };

      recognition.onresult = (event) => {
        let transcript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setMessage(transcript);
      };

      recognition.onerror = () => {
        setListening(false);
      };

      recognition.onend = () => {
        setListening(false);
      };

      recognitionRef.current = recognition;
    }

    recognitionRef.current.start();
  };

  // ------------------------------------------------------------
  // UI
  // ------------------------------------------------------------
  return (
    <div style={styles.wrapper}>
      <textarea
        style={{
          ...styles.input,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? "not-allowed" : "text",
        }}
        rows={1}
        value={message}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
      />

      {/* 🎤 Mic Button */}
      <button
        type="button"
        title={listening ? "Listening…" : "Speak"}
        onClick={startListening}
        disabled={disabled}
        style={{
          ...styles.micButton,
          backgroundColor: listening ? "#dc2626" : "#f3f4f6",
        }}
      >
        🎤
      </button>

      {/* ➤ Send Button (UNCHANGED) */}
      <button
        style={{
          ...styles.sendButton,
          opacity: message.trim() && !disabled ? 1 : 0.4,
          cursor: message.trim() && !disabled ? "pointer" : "not-allowed",
        }}
        onClick={handleSend}
        disabled={!message.trim() || disabled}
        title="Send"
      >
        ➤
      </button>
    </div>
  );
}

// =============================================================================
// Styles
// =============================================================================
const styles = {
  wrapper: {
    display: "flex",
    alignItems: "center",
    padding: "10px",
    borderTop: "1px solid #eee",
    backgroundColor: "#fff",
    gap: "6px",
  },
  input: {
    flex: 1,
    resize: "none",
    borderRadius: "20px",
    border: "1px solid #ddd",
    padding: "10px 14px",
    fontSize: "14px",
    outline: "none",
    lineHeight: "18px",
    maxHeight: "120px",
  },
  micButton: {
    border: "none",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    fontSize: "16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  sendButton: {
    border: "none",
    backgroundColor: "#2563eb",
    color: "#fff",
    borderRadius: "50%",
    width: "36px",
    height: "36px",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
};
